import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { MemberReportService } from 'src/app/services/member-report.service';
import { MessageService } from 'src/app/services/message.service';
import { UserService } from 'src/app/services/user.service';
import { type MemberRow, filterMemberRows, memberRowsToCsv } from 'src/app/utils/member-row';
import { KeepaGraphComponent } from '../keepa-graph/keepa-graph.component';

/**
 * 画面の下の一覧（在庫リストに無い商品）。みんなの仕入れ実績（商品共有）と LINE 報告を、
 * 在庫一覧と同じ形（画像・Keepa グラフ・JAN/ASIN・Amazon 価格と仕入値・利益・商品名）で出し、在庫状況の列だけ無い（2026-10-14 ユーザー指定）
 */
@Component({
  selector: 'app-member-row-table',
  templateUrl: './member-row-table.component.html',
  imports: [CommonModule, ButtonModule, ConfirmDialogModule, TableModule, TooltipModule, KeepaGraphComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService],
})
export class MemberRowTableComponent {
  @Input() rows: MemberRow[] = [];
  /** 在庫一覧の検索窓のキーワード。JAN・ASIN・商品名・報告の行で絞る（2026-10-18 ユーザー指定） */
  @Input() keyword = '';

  /** CSV のファイル名の頭（例: LINE報告 → LINE報告_20261019.csv） */
  @Input() csvName = '一覧';
  /** LINE 報告を消した時に、その報告の id を親に知らせる（パネルの件数を直すため） */
  @Output() reportDeleted = new EventEmitter<number>();

  constructor(
    private userService: UserService,
    private reportService: MemberReportService,
    private confirmationService: ConfirmationService,
    private message: MessageService,
  ) {}

  /** LINE 報告の削除ボタンを出すか。ユーザ管理の権限がある人（管理人）だけ（2026-10-19 ユーザー指定） */
  get canDeleteReports(): boolean {
    return this.userService.hasFuncUser();
  }

  /** LINE 報告を 1 件消す（同じ投稿が二重に入った時など）。確認してから消し、消えたら表からも外す */
  onDeleteReport(m: MemberRow): void {
    if (m.reportId == null) {
      return;
    }
    this.confirmationService.confirm({
      message: `この LINE 報告を消しますか?（JAN ${m.jan}${m.productName ? ' ' + m.productName : ''}）`,
      header: 'LINE 報告の削除',
      acceptLabel: '削除',
      rejectLabel: 'キャンセル',
      accept: async () => {
        try {
          await this.reportService.delete(m.reportId as number);
          this.rows = this.rows.filter((r) => r !== m);
          this.reportDeleted.emit(m.reportId);
        } catch (e) {
          this.message.setFlashMessage('error', 'エラー', `LINE 報告を消せませんでした: ${(e as Error).message ?? e}`);
        }
      },
    });
  }

  get filteredRows(): MemberRow[] {
    return filterMemberRows(this.rows, this.keyword);
  }

  /** いま見えている行（検索で絞った後）をエクセルで開ける CSV（BOM 付き UTF-8）で保存する（2026-10-19 ユーザー指定） */
  downloadCsv(): void {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, memberRowsToCsv(this.filteredRows)], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const d = new Date();
    const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    a.download = `${this.csvName}_${ymd}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
